import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import useLocale from "~/hook/useLocale";
import { CompletedQuizData } from "~/interface/satsang-quiz/satsangQuiz";
import { setIsDialogOpen, setNavigatTo } from "~/lib/store/slice/loginSlice";
import { getCompletedQuizDataThunk } from "~/lib/store/slice/satsangQuizSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { AUTH_COOKIE_TOKEN } from "~/utils/constant";
import { getCookie } from "~/utils/cookies";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import NodataFound from "../shared-components/NoDataFound";
import QuizInfoCard from "./QuizInfoCard";
import { useSearchParams } from "react-router";
import { cn } from "~/lib/utils";

const CompletedQuiz = ({ mode = "desktop" }: { mode?: "mobile" | "desktop" }) => {
  const { completedQuizData } = useAppSelector((state) => state.satsangQuiz);
  const { LocalizedLink } = useLocale();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const searchParamsToken = searchParams.get("token");
  const token = getCookie(AUTH_COOKIE_TOKEN);

  const [pageIndex, setPageIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [allQuizData, setAllQuizData] = useState<CompletedQuizData[]>([]);
  const [isLoginAlertOpen, setIsLoginAlertOpen] = useState(false);

  const PAGE_SIZE = 10;

  useEffect(() => {
    // Initial data fetch
    setIsLoading(true);
    (async () => {
      try {
        const result = await dispatch(
          getCompletedQuizDataThunk({
            mode,
            tempToken: searchParamsToken,
            type: token ? "with_auth" : "without_auth",
            pageSize: PAGE_SIZE,
            pageIndex: 1,
          }),
        );

        if (result.payload) {
          const quizzes = result.payload.rows || [];
          setAllQuizData(quizzes);
          const totalCount = result.payload.count || 0;
          const isAllLoaded = quizzes.length >= totalCount;
          setHasMore(!isAllLoaded);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [token]);

  const fetchMoreData = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const nextPage = pageIndex + 1;
      const result = await dispatch(
        getCompletedQuizDataThunk({
          mode,
          tempToken: searchParamsToken,
          type: token ? "with_auth" : "without_auth",
          pageSize: PAGE_SIZE,
          pageIndex: nextPage,
        }),
      );

      if (result.payload) {
        const newQuizzes = result.payload.rows || [];
        const updatedData = [...allQuizData, ...newQuizzes];
        setAllQuizData(updatedData);
        setPageIndex(nextPage);

        const totalCount = result.payload.count || 0;
        const isAllLoaded = updatedData.length >= totalCount;
        setHasMore(!isAllLoaded);
      }
    } catch (error) {
      console.error("Error fetching more data:", error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AlertDialog open={isLoginAlertOpen} onOpenChange={setIsLoginAlertOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-primaryColor text-xl font-semibold">Login Required</AlertDialogTitle>
            <AlertDialogDescription className="text-smokeGray text-base">Please log in first before you can play this quiz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction
            onClick={() => setIsLoginAlertOpen(false)}
            className="bg-buttonColor hover:bg-primaryColor text-white font-semibold py-2 px-6 rounded transition-colors"
          >
            OK
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>

      {allQuizData?.length === 0 && !isLoading ? (
        <div className="w-full flex justify-center items-center p-4">
          <NodataFound text={t("satsang_quiz.noCompletedQuizzes")} />
        </div>
      ) : (
        <InfiniteScroll
          dataLength={allQuizData.length}
          // next={fetchMoreData}
          next={() => {}}
          hasMore={hasMore}
          scrollThreshold={0.5}
          loader={<div className="w-full justify-center items-center py-8 hidden">{/* <LoadingSpinner /> */}</div>}
        >
          <div className={cn("w-full flex justify-center items-start min-h-[60vh] ", mode === "mobile" ? "pb-52" : "")}>
            <div className={"w-full md:w-[30%] grid grid-cols-1 gap-4"}>
              {allQuizData?.map((quiz, index) => {
                const isAuthenticated = !!(token || searchParamsToken);
                const quizPath =
                  mode === "mobile"
                    ? `/satsang-quiz-m/${quiz.id}${searchParamsToken ? `?token=${searchParamsToken}` : ""}`
                    : `/satsang-quiz/${quiz.id}`;

                return (
                  <LocalizedLink
                    key={index}
                    to={quizPath}
                    onClick={(e) => {
                      if (!isAuthenticated) {
                        e.preventDefault();
                        if (mode === "mobile") {
                          setIsLoginAlertOpen(true);
                          return;
                        }
                        dispatch(setIsDialogOpen());
                        dispatch(setNavigatTo(`/satsang-quiz/${quiz.id}`));
                      }
                    }}
                  >
                    <QuizInfoCard
                      id={quiz.id}
                      title={quiz.title}
                      date={quiz.date}
                      points={quiz.total_point !== 0 ? quiz.total_point : undefined}
                      from="previous_quiz"
                      isPlayed={quiz.played}
                      className="cursor-pointer"
                    />
                  </LocalizedLink>
                );
              })}
            </div>
          </div>
        </InfiniteScroll>
      )}
    </>
  );
};

export default CompletedQuiz;
